import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

interface OverviewPanelProps {
  title: string;
  icon?: ReactNode;
  action?: { label: string; to: string };
  isLoading?: boolean;
  emptyMessage?: string;
  isEmpty?: boolean;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}

export function OverviewPanel({
  title,
  icon,
  action,
  isLoading,
  emptyMessage,
  isEmpty,
  children,
  className,
  bodyClassName,
}: OverviewPanelProps) {
  return (
    <div className={cn('bg-card rounded-lg border border-border shadow-sm', className)}>
      <div className="p-4 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {icon && (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/30">
              {icon}
            </span>
          )}
          <h2 className="text-lg font-semibold text-foreground truncate">{title}</h2>
        </div>
        {action && (
          <Link
            to={action.to}
            className="text-sm font-medium text-primary-600 hover:text-primary-500 shrink-0"
          >
            {action.label}
          </Link>
        )}
      </div>
      <div className={cn('p-4', bodyClassName)}>
        {isLoading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isEmpty ? (
          <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
            {emptyMessage ?? 'Nothing to show'}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
